import React, { useState } from 'react';
import cn from 'classnames';
import { useMatch } from 'react-router';
import Icon from 'src/components/common/Icon';
import { SHARES_AND_SEO, SHARE_CONSTANTS } from 'src/constants/other';
import { RESULTS_ROUTE } from 'src/constants/routes';
import { useAppSelector } from 'src/hooks';
import { selectLoadingResults, selectParties, selectResults, selectShares } from 'src/store/data/selectors';
import { Party, PartyResult } from 'src/types/data-types';
import { analytics } from 'src/utils/analytics';
import { ShareType, share } from 'src/utils/share';

import css from './Socials.module.scss';

type Props = {
  inContent?: boolean;
  className?: string;
};

const findHighestScoreParty = (parties: Record<string, PartyResult>): PartyResult | null => {
  return Object.values(parties).reduce<PartyResult | null>((highest, party) => {
    if (!highest || party.score > highest.score) {
      return party;
    }

    return highest;
  }, null);
};

export const Socials = ({ inContent = false, className = '' }: Props) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const matchResults = useMatch(RESULTS_ROUTE);
  const isResultsRoute = matchResults !== null;
  const isLoadingResults = useAppSelector(selectLoadingResults);
  const results = useAppSelector(selectResults);
  const parties = useAppSelector(selectParties);
  const shares = useAppSelector(selectShares);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleOutsideClick = (e: MouseEvent) => {
      if (!container.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, []);

  const toggleOpenMenu = () => {
    setIsOpen(prevState => !prevState);
  };

  const clickOnShare = (e: React.SyntheticEvent<HTMLDivElement>) => {
    const { type } = e.currentTarget.dataset;

    if (!type) return;

    if (isResultsRoute && inContent && !isLoadingResults) {
      analytics.gtag.event('שיתוף תוצאות', {
        Dynamic_Share: type,
      });
      const heighestParty = findHighestScoreParty(results.parties);
      const heighestScore = heighestParty?.score;
      const partyA = parties.find((p: Party) => p.id == results.parties.partyA.id);
      const partyB = parties.find((p: Party) => p.id == results.parties.partyB.id);

      share({
        type: type as ShareType,
        ogTitle: SHARES_AND_SEO.resultsShares.title
          .replace('{highestMatchParty}', results.winner.name)
          .replace('{highestScore}', `${heighestScore ? Math.round(heighestScore) : ''}`)
          .replace('{partyA}', partyA.name)
          .replace('{partyB}', partyB.name),
        ogDescription: shares.personalized.subtitle,
      });
    } else {
      analytics.gtag.event('שיתוף המתחם', {
        Dynamic_Share: type,
      });

      share({ type: type as ShareType });
    }
  };

  return (
    <>
      <div className={cn(css.socials, inContent && css.inContent, isOpen && css.open, className)} ref={containerRef}>
        {!inContent && (
          <div className={css.toggleBtn} onClick={toggleOpenMenu}>
            <Icon type="share" className={cn(css.icon)} />
          </div>
        )}
        <div className={css.btns}>
          <div className={cn(css.shareIcon)} data-type={SHARE_CONSTANTS.WHATS_APP as ShareType} onClick={clickOnShare}>
            <Icon type="whatsApp" className={cn(css.icon, css.whatsApp)} />
          </div>
          <div className={cn(css.shareIcon)} data-type={SHARE_CONSTANTS.FACEBOOK as ShareType} onClick={clickOnShare}>
            <Icon type="facebook" className={cn(css.icon, css.facebook)} />
          </div>
          <div className={cn(css.shareIcon)} data-type={SHARE_CONSTANTS.TWITTER as ShareType} onClick={clickOnShare}>
            <Icon type="x" className={cn(css.icon, css.twitter)} />
          </div>
        </div>
      </div>
    </>
  );
};
